import axios, { AxiosInstance, AxiosError } from 'axios'
import { acquireAccessToken } from '../services/access-token-service'
import { setApiVersion } from '../stores/api-info-store'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5050'

export interface ApiError {
  status: number
  message: string
  errors?: Record<string, string[]>
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to attach Bearer token and dev role override
apiClient.interceptors.request.use(async (config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = config.headers as Record<string, unknown> & {
      delete?: (name: string) => void
    }
    headers.delete?.('Content-Type')
    headers.delete?.('content-type')
    delete headers['Content-Type']
    delete headers['content-type']
  }

  const token = await acquireAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (import.meta.env.DEV) {
    const roleOverride = sessionStorage.getItem('dev-role-override')
    if (roleOverride) {
      config.headers['X-Dev-Role-Override'] = roleOverride
    }
  }
  return config
})

// Response interceptor for capturing API version and error handling
apiClient.interceptors.response.use(
  (response) => {
    const version = response.headers['x-version-api']
    if (version) setApiVersion(version)
    return response
  },
  (error: AxiosError<{ errors?: Record<string, string[]>; title?: string }>) => {
    const status = error.response?.status || 500
    const apiError: ApiError = {
      status,
      message:
        status === 403
          ? 'You do not have permission to perform this action'
          : error.response?.data?.title || error.message || 'An error occurred',
      errors: error.response?.data?.errors,
    }
    return Promise.reject(apiError)
  }
)
